import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  COOKIE_KEY_READER_FONT_SIZE,
  COOKIE_KEY_SEARCH_FONT_SIZE,
  COOKIE_KEY_THEME,
  DefaultFontSize,
  getIsAccessibilityTheme,
  ReaderFontSizes,
  activateTheme,
  isDarkTheme, Theme,
} from "@/shared/utils/theme/theme";
import { VIEW_MODE } from "@/shared/utils/viewmode";
import { SEARCH_FONT_SIZE_DEFAULT } from "@/components/SearchResults/Search.constants";
import { settingsActions } from "@/store/actions";
import useCookiesWithOptions from "@/shared/hooks/useCookiesWithOptions";
import { selectorSetting } from "@/shared/store/settings/selectors";
import { SETTING } from "@/shared/utils/settings/constants";
import { DefaultThemeIdEasyModeApp } from "@/utils/theme";
import { validateSettingNextApp } from "@/utils/settings/settings";
import { getCookie } from "@/shared/utils/storage/storage";
import { READER_FONT_SIZE_DEFAULT } from "@/utils/reader/constants";

export const ZoomCoefficientAcc = 1.1;

const isViewModeAccessibilityNow = () => {
  return document.body.classList.contains(VIEW_MODE.BLIND);
};

const regulateBlindModeProperties = (themeId) => {
  const rootElement = document.querySelector(":root");
  const classList = document.body.classList;

  if (getIsAccessibilityTheme(themeId)) {
    classList.add(VIEW_MODE.BLIND);
    rootElement.style.fontSize = DefaultFontSize * ZoomCoefficientAcc + "px";
  } else {
    classList.remove(VIEW_MODE.BLIND);
    rootElement.style.fontSize = DefaultFontSize + "px";
  }
};

export const useRegulateTheme = ({ isLanding, landingBookViewConfig }) => {
  const dispatch = useDispatch();
  const themeMode = useSelector(selectorSetting(SETTING.themeMode.id));
  const [, setCookie] = useCookiesWithOptions([COOKIE_KEY_THEME]);
  const isAppHasSingleTheme = isLanding && !landingBookViewConfig?.hasDarkMode;

  const updateTheme = useCallback(
    (themeId) => {
      if (!isAppHasSingleTheme) {
        const isAccessibilityTheme = getIsAccessibilityTheme(themeId);

        setCookie(COOKIE_KEY_THEME, themeId);

        if (isAccessibilityTheme !== isViewModeAccessibilityNow()) {
          const coefficientSearchFontSizeAccToNormal = 22 / SEARCH_FONT_SIZE_DEFAULT;
          const coefficientReaderFontSizeAccToNormal =
            ReaderFontSizes.blindDefault / READER_FONT_SIZE_DEFAULT;

          const searchFontSizeCurr = getCookie(COOKIE_KEY_SEARCH_FONT_SIZE, document.cookie);
          const readerFontSizeCurr = getCookie(COOKIE_KEY_READER_FONT_SIZE, document.cookie);

          setCookie(
            COOKIE_KEY_SEARCH_FONT_SIZE,
            isAccessibilityTheme
              ? searchFontSizeCurr * coefficientSearchFontSizeAccToNormal
              : searchFontSizeCurr / coefficientSearchFontSizeAccToNormal,
          );
          setCookie(
            COOKIE_KEY_READER_FONT_SIZE,
            isAccessibilityTheme
              ? readerFontSizeCurr * coefficientReaderFontSizeAccToNormal
              : readerFontSizeCurr / coefficientReaderFontSizeAccToNormal,
          );
        }
      }

      activateTheme(themeId);

      regulateBlindModeProperties(themeId);

      dispatch(settingsActions.updateSettings(
        SETTING.themeMode.id, themeId, !isLanding,
      ));
    },
    [isAppHasSingleTheme, isLanding, setCookie],
  );

  useEffect(() => {
    if (isAppHasSingleTheme) {
      updateTheme(landingBookViewConfig?.defaultTheme);
    } else {
      // Fast-fix:
      // Exception for {Theme.ls}, to prevent "ls" theme for landing site,
      // but though be able to use "ls" theme for specific landing apps.
      const isValidTheme = themeMode !== Theme.ls.id
        && validateSettingNextApp(SETTING.themeMode.id, themeMode);

      if (isValidTheme) {
        updateTheme(themeMode);
      } else {
        updateTheme(isLanding ? landingBookViewConfig?.defaultTheme : DefaultThemeIdEasyModeApp);
      }
    }
  }, [isAppHasSingleTheme, isLanding, themeMode, updateTheme]);
};

export const useTheme = () => {
  const dispatch = useDispatch();
  const themeMode = useSelector(selectorSetting(SETTING.themeMode.id));

  const updateTheme = (themeId) => {
    if (themeId) {
      document.body.className = document.body.className.replace(themeMode, themeId);
      dispatch(settingsActions.updateSettings(SETTING.themeMode.id, themeId));
    }
  };

  return {
    activeTheme: themeMode,
    updateTheme,
    isDarkTheme: isDarkTheme(themeMode),
  };
};
